import React from "react";
import {Redirect, Route, Switch} from "react-router-dom";
import {useBooleanFeatureFlag} from "ub-feature-flagging-react";

import PasswordResetRoutes from "shared/PasswordReset/PasswordResetRoutes";
import {CHAT_NORMALISATION_ENABLED, CHAT_SERVICE_FLOW_ENABLED} from "shared/constants";
import {Captcha} from "shared/Captcha/Captcha";

import requireExternalAuth from "../../../../../shared/hoc/requireExternalAuth";
import ApplicantInbox from "../../../../../modules/Inbox/components/ApplicantInbox/ApplicantInbox";
import ApplicantInboxLegacy from "../../../../../modules/Inbox/components/ApplicantInbox/ApplicantInboxLegacy";
import Logout from "../../../../../modules/Logout/Logout";
import {ChatLegacy} from "../../../../../modules/Chat/containers/Chat/ChatLegacy";
import Verify from "../../../../../modules/Verification/Verify";
import VerifyChangeEmail from "../../../../../modules/Verification/VerifyChangeEmail/VerifyChangeEmail";
import {AppContainer} from "../../../../../shared/components/Containers/AppContainer";
import {StartChatLegacy} from "../../../../../modules/Chat/containers/StartChat/StartChatLegacy";
import {StartChat} from "../../../../../modules/Chat/containers/StartChat/StartChat";
import DummyChat from "../../../../../modules/Chat/containers/DummyChat/DummyChat";
import {RegisterPage2Wrapper} from "../../../../../modules/RegisterNew/components/RegisterPage2Wrapper";
import {RegisterPage1} from "../../../../../modules/RegisterNew/containers/RegisterPage1";
import {RegisterPage2} from "../../../../../modules/RegisterNew/containers/RegisterPage2";
import {LoginPage} from "../../../../../modules/RegisterNew/containers/LoginPage";
import {AgeVerificationPage} from "../../../../../modules/RegisterNew/containers/AgeVerificationPage";
import {AgeVerificationWrapper} from "../../../../../modules/RegisterNew/components/AgeVerificationWrapper";
import {MinorPage} from "../../../../../modules/RegisterNew/containers/MinorPage";
import {SocketDelinkListener} from "../../../../../modules/SocketWrapperProvider/SocketDelinkListener";
import {ChatUrlNormaliser} from "../../../../../modules/Chat/utils/ChatUrlNormaliser";

const ExternalHeaderRoutes = (parentProps) => {
    const enableTestChatNormalisation = useBooleanFeatureFlag(
        CHAT_NORMALISATION_ENABLED.key,
        CHAT_NORMALISATION_ENABLED.value,
    );
    const isChatServiceEnabled = useBooleanFeatureFlag(
        CHAT_SERVICE_FLOW_ENABLED.key,
        CHAT_SERVICE_FLOW_ENABLED.value,
    );

    const isChatFlowEnabled = isChatServiceEnabled && enableTestChatNormalisation;

    return (
        <>
            <SocketDelinkListener />
            <Switch>
                <Route
                    exact
                    path={`${parentProps.match.path}/auth`}
                    render={(props) => (
                        <AppContainer>
                            <Captcha>
                                <RegisterPage1
                                    primaryApp={props.primaryApp ?? parentProps.primaryApp}
                                />
                            </Captcha>
                        </AppContainer>
                    )}
                />

                <Route
                    exact
                    path={`${parentProps.match.path}/auth/verify/age`}
                    render={() => <AgeVerificationPage />}
                />

                <Route
                    exact
                    path={`${parentProps.match.path}/auth/minor`}
                    render={() => <MinorPage />}
                />

                <Route
                    exact
                    path={`${parentProps.match.path}/auth/login`}
                    render={() => (
                        <AppContainer>
                            <LoginPage />
                        </AppContainer>
                    )}
                />
                <Route
                    exact
                    path={`${parentProps.match.path}/auth/logout`}
                    render={(props) => (
                        <AppContainer>
                            <Logout {...parentProps} {...props} />
                        </AppContainer>
                    )}
                />
                <Route
                    exact
                    path={`${parentProps.match.path}/auth/register`}
                    render={(props) => (
                        <AppContainer>
                            <Captcha>
                                <RegisterPage1
                                    primaryApp={props.primaryApp ?? parentProps.primaryApp}
                                />
                            </Captcha>
                        </AppContainer>
                    )}
                />
                <Route
                    exact
                    path={`${parentProps.match.path}/auth/register2`}
                    render={requireExternalAuth(() => (
                        <AppContainer>
                            <AgeVerificationWrapper>
                                <RegisterPage2 />
                            </AgeVerificationWrapper>
                        </AppContainer>
                    ))}
                />
                <Route
                    path={`${parentProps.match.url}/auth/reset`}
                    render={(props) => (
                        <AppContainer>
                            <PasswordResetRoutes {...parentProps} {...props} />
                        </AppContainer>
                    )}
                />
                <Route
                    exact
                    path={`${parentProps.match.path}/auth/verify`}
                    render={(props) => (
                        <AppContainer>
                            <Captcha>
                                <Verify {...parentProps} {...props} />
                            </Captcha>
                        </AppContainer>
                    )}
                />
                <Route
                    exact
                    path={`${parentProps.match.path}/auth/verify/change`}
                    render={(props) => (
                        <AppContainer>
                            <Captcha>
                                <VerifyChangeEmail {...parentProps} {...props} />
                            </Captcha>
                        </AppContainer>
                    )}
                />
                <Route
                    exact
                    path={`${parentProps.match.path}/inbox`}
                    render={requireExternalAuth((props) => (
                        <RegisterPage2Wrapper>
                            <AppContainer>
                                {isChatFlowEnabled ? (
                                    <ApplicantInbox isExternal {...parentProps} {...props} />
                                ) : (
                                    <ApplicantInboxLegacy isExternal {...parentProps} {...props} />
                                )}
                            </AppContainer>
                        </RegisterPage2Wrapper>
                    ))}
                />
                <Route
                    exact
                    path={`${parentProps.match.path}/inbox/pre/:opponentId`}
                    render={(props) => (
                        <AppContainer>
                            <DummyChat isExternal {...parentProps} {...props} />
                        </AppContainer>
                    )}
                />
                <Route
                    exact
                    path={`${parentProps.match.path}/inbox/start/:opponentId`}
                    render={requireExternalAuth(() => (
                        <RegisterPage2Wrapper>
                            <AppContainer>
                                {isChatFlowEnabled ? <StartChat /> : <StartChatLegacy />}
                            </AppContainer>
                        </RegisterPage2Wrapper>
                    ))}
                />
                {enableTestChatNormalisation ? (
                    <Route
                        path={`${parentProps.match.path}/inbox/:id`}
                        render={requireExternalAuth((routeProps) => (
                            <ChatUrlNormaliser {...routeProps} />
                        ))}
                    />
                ) : (
                    <Route
                        exact
                        path={`${parentProps.match.path}/inbox/:chatGroupId`}
                        render={requireExternalAuth((props) => (
                            <RegisterPage2Wrapper>
                                <AppContainer>
                                    <ChatLegacy {...parentProps} {...props} />
                                </AppContainer>
                            </RegisterPage2Wrapper>
                        ))}
                    />
                )}
                <Route exact path={`${parentProps.match.path}`}>
                    <Redirect
                        to={
                            parentProps.match.url?.endsWith("/")
                                ? "inbox"
                                : `${parentProps.match.url}/inbox`
                        }
                    />
                </Route>
            </Switch>
        </>
    );
};

export default ExternalHeaderRoutes;
